import { FC, useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search } from 'lucide-react';
import { VoucherSearchProps } from './VoucherSearch.types';
import { VoucherSearchFilters } from '../../types';
import { useAccessibleLocations } from '@/hooks/useAccessibleLocations';
import { useAuth } from '@/context/AuthContext';
import { TextKeypad, NumericKeypad } from '@/components/ui/keypads';

// Calcular rango lunes-domingo de la semana actual
const getWeekRange = () => {
  const now = new Date();
  const day = now.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { dateFrom: fmt(monday), dateTo: fmt(sunday) };
};

export const VoucherSearch: FC<VoucherSearchProps> = ({ onSearch, loading = false }) => {
  const { user } = useAuth();
  const isCajero = user?.positionId === 3;
  const [seriesKeypadOpen, setSeriesKeypadOpen] = useState(false);
  const [numberKeypadOpen, setNumberKeypadOpen] = useState(false);

  const weekRange = getWeekRange();
  const [filters, setFilters] = useState<VoucherSearchFilters>({
    dateFrom: weekRange.dateFrom,
    dateTo: weekRange.dateTo,
    voucherType: '',
    status: '',
    locationId: '',
    series: '',
    number: ''
  });

  const { locationsWithAll: locations } = useAccessibleLocations();
  const initialSearchDone = useRef(false);

  const buildCleanFilters = (source: VoucherSearchFilters): VoucherSearchFilters => {
    const clean: VoucherSearchFilters = {};
    if (source.dateFrom) clean.dateFrom = source.dateFrom;
    if (source.dateTo) clean.dateTo = source.dateTo;
    if (source.voucherType) clean.voucherType = source.voucherType as 'boleta' | 'factura';
    if (source.status) clean.status = source.status as 'emitido' | 'anulado';
    if (source.locationId) clean.locationId = source.locationId;
    if (source.series) clean.series = source.series;
    if (source.number) clean.number = source.number;
    return clean;
  };

  // Auto-seleccionar local si el usuario solo tiene uno y ejecutar búsqueda inicial
  useEffect(() => {
    if (locations.length === 0 || initialSearchDone.current) return;

    initialSearchDone.current = true;

    // Si solo tiene un local (sin contar "Todos"), auto-seleccionar y buscar con ese local
    if (locations.length === 1 && locations[0].id !== 'all') {
      const locationId = locations[0].id.toString();
      setFilters(prev => ({ ...prev, locationId }));
      onSearch(buildCleanFilters({ ...filters, locationId }));
    } else {
      onSearch(buildCleanFilters(filters));
    }
  }, [locations]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(buildCleanFilters(filters));
  };


  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4">Buscar Comprobantes</h3>

      <form onSubmit={handleSearch} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="dateFrom">Fecha Desde</Label>
            <Input
              id="dateFrom"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
            />
          </div>

          <div>
            <Label htmlFor="dateTo">Fecha Hasta</Label>
            <Input
              id="dateTo"
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
            />
          </div>

          <div>
            <Label htmlFor="voucherType">Tipo</Label>
            <Select
              value={filters.voucherType || 'all'}
              onValueChange={(value) => setFilters(prev => ({ ...prev, voucherType: value === 'all' ? '' : value }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="boleta">Boleta</SelectItem>
                <SelectItem value="factura">Factura</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="status">Estado</Label>
            <Select
              value={filters.status || 'all'}
              onValueChange={(value) => setFilters(prev => ({ ...prev, status: value === 'all' ? '' : value }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="emitido">Emitido</SelectItem>
                <SelectItem value="anulado">Anulado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="locationId">Local</Label>
            <Select
              value={filters.locationId || 'all'}
              onValueChange={(value) => setFilters(prev => ({ ...prev, locationId: value === 'all' ? '' : value }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id.toString()}>
                    {location.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="series">Serie</Label>
            <Input
              id="series"
              value={filters.series}
              onChange={!isCajero ? (e) => setFilters(prev => ({ ...prev, series: e.target.value.toUpperCase() })) : undefined}
              readOnly={isCajero}
              onClick={isCajero ? () => setSeriesKeypadOpen(true) : undefined}
              placeholder="Ej: F001, B001"
              className={isCajero ? 'cursor-pointer' : ''}
            />
          </div>

          <div>
            <Label htmlFor="number">Número</Label>
            <Input
              id="number"
              value={filters.number}
              onChange={!isCajero ? (e) => setFilters(prev => ({ ...prev, number: e.target.value })) : undefined}
              readOnly={isCajero}
              onClick={isCajero ? () => setNumberKeypadOpen(true) : undefined}
              placeholder="Número de comprobante"
              className={isCajero ? 'cursor-pointer' : ''}
            />
          </div>

          <div className="flex items-end">
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-macartur-primary hover:bg-macartur-primary/90"
            >
              <Search className="h-4 w-4 mr-2" />
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>
        </div>
      </form>

      {/* Keypads para cajero */}
      {isCajero && (
        <>
          <TextKeypad
            isOpen={seriesKeypadOpen}
            onClose={() => setSeriesKeypadOpen(false)}
            onSubmit={(value) => {
              setFilters(prev => ({ ...prev, series: value.toString().toUpperCase() }));
              setSeriesKeypadOpen(false);
            }}
            title="Serie"
            initialValue={filters.series || ''}
          />
          <NumericKeypad
            isOpen={numberKeypadOpen}
            onClose={() => setNumberKeypadOpen(false)}
            onSubmit={(value) => {
              setFilters(prev => ({ ...prev, number: value ? value.toString() : '' }));
              setNumberKeypadOpen(false);
            }}
            title="Número de Comprobante"
            initialValue={filters.number ? parseInt(filters.number) : 0}
          />
        </>
      )}
    </Card>
  );
};